/**
 * CellCog's OWN remote MCP server (CEL-2750, design §10.3/§10.4). One home for the two facts the
 * React side repeats: the URL users paste into an MCP client, and the plain-words label of each
 * OAuth scope. The backend returns the same labels on the consent request; this map is the
 * fallback for surfaces that only have scope ids (the connected-apps list).
 */
export const MCP_SERVER_URL = 'https://cellcog.ai/mcp';

export const MCP_CLAUDE_CODE_COMMAND = `claude mcp add --transport http cellcog ${MCP_SERVER_URL}`;

// Codex CLI (OpenAI): the same address as a remote server; `codex mcp list` verifies it.
export const MCP_CODEX_COMMAND = `codex mcp add cellcog --url ${MCP_SERVER_URL}`;

// Codex signs in to a remote server with a separate step (developers.openai.com/codex/mcp, read Oct 1 2026):
// the add alone leaves CellCog listed but not authorized. Delegate v3 (CEL-3067) shows both commands.
export const MCP_CODEX_LOGIN_COMMAND = 'codex mcp login cellcog';

// Cursor's official install link (cursor.com/docs/mcp/install-links): name + base64(JSON of the SERVER entry,
// type + url, not the mcpServers wrapper). The https form routes into the Cursor app and survives browsers that
// block the cursor:// scheme (cursor.com/install-mcp answers 200; the /en/ variant is a redirect). Built at module
// load so the address and the link can never disagree. Delegate app, Sep 21 2026.
const cursorConfig = typeof btoa === 'function' ? btoa(JSON.stringify({ type: 'streamable-http', url: MCP_SERVER_URL })) : '';
export const MCP_CURSOR_INSTALL_URL = `https://cursor.com/install-mcp?name=CellCog&config=${encodeURIComponent(cursorConfig)}`;

export const MCP_SCOPE_LABELS = {
  'chats:write': 'Create chats, hire AI employees and send messages in your account',
  'chats:read': 'Read chat status and results',
  'files:read': 'Open files the agent produced (links valid for 30 days)',

};

/** True when the client build knows this scope (a label exists, or the API sent one). A bare id with no label is a
 *  retired or unknown scope: the connected-apps list skips it instead of printing the raw id (Sep 21 2026, the
 *  "ab:write" chip; the backend normalizes stored scopes too, this is the second wall). */
export const isKnownScope = (scope) => {
  if (!scope) return false;
  if (typeof scope === 'object') return !!(scope.label || MCP_SCOPE_LABELS[scope.id]);
  return !!MCP_SCOPE_LABELS[scope];
};

/** A scope entry from the API is either `{id, label}` or a bare id string; both render as a label. */
export const scopeLabel = (scope) => {
  if (!scope) return '';
  if (typeof scope === 'object') return scope.label || MCP_SCOPE_LABELS[scope.id] || scope.id || '';
  return MCP_SCOPE_LABELS[scope] || scope;
};

/** Host of a client URI for a compact trust hint ("claude.ai"); the raw string when it does not parse. */
export const hostOf = (uri) => {
  if (!uri) return '';
  try {
    return new URL(uri).host || uri;
  } catch (e) {
    return uri;
  }
};
